import { FC, useMemo } from 'react';
import {
  useGetTodayScheduledCallsQuery,
  useGetUserProjectsQuery,
  useGetCurrentUserQuery,
  UserProject,
  UserProjectStatus,
  ScheduledCall,
} from '@teachur/http-client';
import { useVideoCallContext } from '../../../../../contexts/VideoCallProvider';
import { DashboardList } from '../DashboardList/DashboardList';
import { mapCallToTodayItem, mapProjectToPendingItem } from '../../lib/helpers';
import { PROJECTS_PAGE_SIZE, DASHBOARD_LIST_LIMIT, MILLISECONDS_PER_DAY } from '../../constants';

const DUE_DAYS = 5;

interface TodayAgendaProps {
  onSelectProject: (project: UserProject) => void;
  onSelectCall?: (call: ScheduledCall) => void;
}

export const TodayAgenda: FC<TodayAgendaProps> = ({ onSelectProject, onSelectCall }) => {
  const { openVideoCall } = useVideoCallContext();
  const { data: currentUser } = useGetCurrentUserQuery();
  const { data: todayData } = useGetTodayScheduledCallsQuery();
  const currentUserId = currentUser?.id;

  const { data: projectsData } = useGetUserProjectsQuery(
    currentUserId
      ? {
          status: UserProjectStatus.InReview,
          instructorId: currentUserId,
          page: 1,
          pageSize: PROJECTS_PAGE_SIZE,
        }
      : undefined
  );

  const todayCalls = todayData?.data ?? [];
  const inReviewProjects = projectsData?.data ?? [];

  const myActiveProjects = useMemo(() => {
    return inReviewProjects.filter((project) => {
      if (!project.claimedAt) return false;
      const daysElapsed = Math.floor(
        (Date.now() - new Date(project.claimedAt).getTime()) / MILLISECONDS_PER_DAY
      );
      return daysElapsed <= DUE_DAYS;
    });
  }, [inReviewProjects]);

  const items = useMemo(
    () =>
      [
        ...todayCalls.map((call) => mapCallToTodayItem(call, openVideoCall, onSelectCall)),
        ...myActiveProjects.map((project) => mapProjectToPendingItem(project, onSelectProject)),
      ].slice(0, DASHBOARD_LIST_LIMIT),
    [todayCalls, myActiveProjects, openVideoCall, onSelectProject, onSelectCall]
  );

  return <DashboardList title="Today's Agenda" items={items} variant="today" className="flex-1" />;
};
